<template>
  <section class="ma-4 ma-md-8">
    <t-floating-bar v-if="eventFilter.resource?.value">
      {{ eventName(eventFilter.resource.value) }}
    </t-floating-bar>
    <t-page-title
      title="Vendas | Relatório de Ingressos"
      tooltip="São exibidas apenas vendas realizadas nos canais: site de vendas e POS"
      :on-report="() => datatable?.openDialogReport()"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :report="report"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :pagination="pagination"
          :metadata="metadata"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.order_number`]="{ item }">
            <span> {{ item?.order?.order_number ?? "" }} </span>
          </template>
          <template #[`item.buyer_name`]="{ item }">
            <span>
              {{ item?.order?.owner?.name ?? item.holder ?? "" }}
            </span>
          </template>
          <template #[`item.payment_method`]="{ item }">
            <span>
              {{ item?.order?.payment_method?.name ?? "Caixa" }}
            </span>
          </template>
          <template #[`item.value`]="{ item }">
            <span>
              {{ $formatters.currency(parseFloat(item.passport.batch.price)) }}
            </span>
          </template>
          <template #[`item.convenience_fee`]="{ item }">
            <span> {{ $formatters.currency(item?.fee ?? 0) }} </span>
          </template>
          <template #[`item.discount`]="{ item }">
            <span> {{ $formatters.currency(item?.discount ?? 0) }} </span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span>
              {{
                $luxon.DateTime.fromSQL(item.created_at).toFormat(
                  "dd/MM/yyyy HH:mm:ss"
                )
              }}</span>
          </template>
          <template #[`item.pos`]="{ item }">
            <span>
              {{
                item.passport.ticket_sale.pos?.name ??
                  (item.passport.origin === "venda_online" ? "Venda Online" : "")
              }}
            </span>
          </template>
          <template #[`item.read_at`]="{ item }">
            <span>
              {{ item.passport.reading ? fromSQL(item.passport.reading.created_at) : '' }}
            </span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { Ref, ref, useTemplateRef } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import SaleItemService from '@/services/reports/SaleItemService'
import type {
  DataTableHeader,
  DataTableMeta,
  DataTablePagination,
  DataTableMetaItem,
  DataTableFilter,
} from '@/types/DataTable'

import type { Report } from '@/types/Report'
import SaleItemsService from '@/services/reports/SaleItemService'
import { currency } from '@/helpers/formatters/currency'
import { useSnackBar } from '@/stores/snackbar'
import { useProducerStore } from '@/stores/producer'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useSectorFilter } from '@/helpers/reports/sectorFilter'
import { usePointOfSaleFilter } from '@/helpers/reports/pointOfSaleFilter'
import { usePaymentMethodFilter } from '@/helpers/reports/paymentMethodFilter'
import DataTable from '@/components/datatable/DataTable.vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import { useAbility } from '@casl/vue'
import { eventName } from '@/plugins/eventName'
import { fromSQL } from '@/plugins/date'

const { can } = useAbility()
const snackbar = useSnackBar()

const datatable = useTemplateRef('datatable')

const headers: DataTableHeader[] = [
  { title: 'Número da compra', value: 'order_item.order.order_number', key: 'order_number', sortable: false },
  { title: 'Comprador', value: 'order_item.order.owner.name', key: 'buyer_name', sortable: false },
  { title: 'Forma de pagamento', value: 'order_item.order.payment_method.name', key: 'payment_method', sortable: false, align: 'center' },
  { title: 'Valor', value: '', key: 'value', sortable: false, align: 'center' },
  { title: 'Desconto', value: '', key: 'discount', sortable: false, align: 'center' },
  { title: 'Taxa de serviço', value: '', key: 'convenience_fee', sortable: false, align: 'center', show: () => can('read', 'fees') },
  { title: 'Setor', value: 'passport.ticket_type.sector_name', key: 'passport.ticket_type.sector_id', sortable: false, align: 'center' },
  { title: 'Tipo de ingresso', value: 'passport.ticket_type.name', key: 'passport.ticket_type.id', sortable: false, align: 'center' },
  { title: 'Data da venda', value: 'created_at', key: 'created_at', sortable: false, align: 'center' },
  { title: 'Ponto de venda', value: 'pos', key: 'pos', sortable: false, align: 'center' },
  { title: 'Horário da Leitura', value: 'passport.reading.created_at', key: 'read_at', sortable: false, align: 'center' },
]

const report: Ref<Report> = ref({
  filename: 'relatorio_vendas_ingressos',
  provider: async (filters: string, selected) => {
    return await new SaleItemsService().report(filters, selected)
  },
  items: [
    { label: 'ID', field: 'id', active: true },
    { label: 'Evento', field: 'event.title', active: true },
    { label: 'Número da compra', field: 'order.order_number', active: true },
    { label: 'Nome do comprador', field: 'order.owner.name', active: true },
    { label: 'E-mail do comprador', field: 'order.owner.email', active: true },
    { label: 'Telefone do comprador', field: 'order.owner.cellphone', active: true },
    { label: 'Forma de pagamento', field: 'order.payment_method.name', active: true },
    { label: 'Valor', field: 'passport.batch.price', active: true },
    { label: 'Taxa de serviço', field: 'fee', active: true, show: can('read', 'fees') },
    { label: 'Setor', field: 'passport.ticket_type.sector_name', active: true },
    { label: 'Tipo de ingresso', field: 'passport.ticket_type.name', active: true },
    { label: 'Código', field: 'passport.ticket_sale.code', active: true },
    { label: 'Data da venda', field: 'created_at', active: true },
    { label: 'Ponto de venda', field: 'passport.ticket_sale.pos.name', active: true },
    { label: 'Vendedor', field: 'order.seller.name', active: true },
    { label: 'Caixa', field: 'order.terminal.name', active: true },
    { label: 'Hora de Leitura', field: 'passport.reading.created_at', active: true },
  ],
})

const metadata: DataTableMeta = {
  headers: [
    {
      title: 'Quantidade',
      value: 'quantity',
      key: 'quantity',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Valor Nominal',
      value: 'subtotal',
      key: 'subtotal',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Descontos',
      value: 'subtotal_discounts',
      key: 'subtotal_discounts',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Taxas',
      value: 'subtotal_fees',
      key: 'subtotal_fees',
      align: 'center',
      sortable: false,
      show: () => can('read', 'fees'),
    },
  ],
  items: [] as DataTableMetaItem[],
}

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  field: 'event_id',
  type: 'autocomplete',
  label: 'Evento',
})

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  useSectorFilter({ event: eventFilter.model, field: 'sector_id', label: 'Setor', multiple: true }),
  usePointOfSaleFilter({ producer: ref(useProducerStore().producer!), field: 'point_of_sale_id', label: 'Ponto de Venda', multiple: true }),
  usePaymentMethodFilter({ label: 'Método de Pagamento', field: 'payment_method', multiple: true }),
  useTextFilter({ label: 'Número da Compra', field: 'order_number', required: false }),
  useTextFilter({ label: 'Código do Ingresso', field: 'code', required: false }),
  useTextFilter({ label: 'Nome do Comprador', field: 'buyer_name', required: false }),
  useTextFilter({ label: 'CPF do Comprador', field: 'buyer_document', required: false }),
  useDateIntervalFilter({
    field: 'date',
    label: 'Período',
    required: false,
    initial: null,
  }),
])

const pagination: Ref<DataTablePagination> = ref({
  page: 1,
  itemsPerPage: 50,
  total: 0,
  totalPages: 0,
})

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await new SaleItemService().index(
      page,
      filters,
      per_page,
    )

    const additional = response.data.additional!
    metadata.items = [
      {
        quantity: additional.quantity as number,
        subtotal: currency(additional.subtotal),
        subtotal_fees: currency(additional.total_fees),
        subtotal_discounts: currency(additional.total_discounts),
        total: currency(
          additional.subtotal - additional.total_fees,
        ),
      },
    ]

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar os ingressos', 'error')

    throw error
  }
}
</script>
